import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const RetailEcommerce = () => {
  return (
    <>
      <SEO 
        title="Retail & E-commerce Solutions - AI-Powered Digital Commerce"
        description="Transform your retail business with AI-powered e-commerce solutions. Personalized shopping experiences, inventory optimization, omnichannel integration, and advanced analytics for retail excellence."
        keywords="retail AI solutions, e-commerce platforms, inventory management, customer analytics, omnichannel retail, personalization engine, retail automation, digital commerce"
      />

      {/* Hero Section */}
      <section className="pt-10 pb-10 md:pb-10 bg-gradient-to-br from-pearl-white via-white to-blush-light/10">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-primary-red/10 rounded-full mb-6 animate-fade-in">
              <span className="text-primary-red font-semibold text-sm">Retail & E-commerce Solutions</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-slide-up">
              Transform Retail with <span className="text-gradient">AI-Powered Commerce</span>
            </h1>
            <p className="text-xl text-titanium leading-relaxed mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Elevate customer experiences, optimize operations, and drive revenue growth with intelligent retail solutions designed for the digital age.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/contact" className="btn-primary">
                Start Your Transformation
              </Link>
              <Link to="/ai-hub" className="btn-secondary">
                Explore AI Capabilities
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-5 bg-white border-b border-platinum">
        <div className="page-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary-red mb-2">35%</div>
              <div className="text-sm text-titanium">Avg. Conversion Rate Increase</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary-red mb-2">40%</div>
              <div className="text-sm text-titanium">Reduction in Cart Abandonment</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary-red mb-2">50%</div>
              <div className="text-sm text-titanium">Inventory Cost Optimization</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary-red mb-2">60%</div>
              <div className="text-sm text-titanium">Faster Order Fulfillment</div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Challenges */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Retail Challenges We Solve
            </h2>
            <p className="text-lg text-titanium">
              Address critical pain points in modern retail and e-commerce operations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Fragmented Customer Data",
                description: "Disconnected systems preventing unified customer views and personalization",
                icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              },
              {
                title: "Inventory Management",
                description: "Stock imbalances, overstock situations, and lost sales from stockouts",
                icon: "M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              },
              {
                title: "Cart Abandonment",
                description: "High abandonment rates due to poor checkout experiences and lack of engagement",
                icon: "M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
              },
              {
                title: "Omnichannel Gaps",
                description: "Inconsistent experiences across online, mobile, and physical store channels",
                icon: "M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
              },
              {
                title: "Price Optimization",
                description: "Manual pricing strategies missing dynamic market opportunities and margin optimization",
                icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              },
              {
                title: "Customer Insights",
                description: "Limited understanding of customer behavior, preferences, and lifetime value",
                icon: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              }
            ].map((challenge, index) => (
              <div key={index} className="card group hover:shadow-xl hover:scale-105 transition-all duration-300">
                <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary-red/10 transition-colors">
                  <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={challenge.icon} />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-charcoal">{challenge.title}</h3>
                <p className="text-sm text-titanium">{challenge.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Solutions */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Comprehensive Retail Solutions
            </h2>
            <p className="text-lg text-titanium">
              End-to-end technology stack for modern retail and e-commerce operations
            </p>
          </div>

          <div className="space-y-12">
            {/* Solution 1 */}
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-block px-3 py-1 bg-primary-red/10 rounded-full mb-4">
                  <span className="text-primary-red font-semibold text-sm">AI-Powered Personalization</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4 text-charcoal">
                  Intelligent Shopping Experiences
                </h3>
                <p className="text-titanium mb-6">
                  Deliver hyper-personalized product recommendations, content, and offers that resonate with each customer's unique preferences and behavior patterns.
                </p>
                <ul className="space-y-3">
                  {[
                    "Real-time behavioral analysis and product recommendations",
                    "Dynamic content personalization across all touchpoints",
                    "AI-powered search with visual and voice capabilities",
                    "Predictive customer segmentation and targeting",
                    "A/B testing and conversion optimization"
                  ].map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-graphite">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-alabaster to-pearl-white rounded-2xl p-8 lg:p-12">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: "Click-Through Rate", value: "+45%" },
                    { label: "Avg. Order Value", value: "+32%" },
                    { label: "Customer Engagement", value: "+55%" },
                    { label: "Return Visits", value: "+40%" }
                  ].map((metric, index) => (
                    <div key={index} className="bg-white p-4 rounded-xl shadow-sm text-center">
                      <div className="text-2xl font-bold text-primary-red mb-1">{metric.value}</div>
                      <div className="text-xs text-titanium">{metric.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Solution 2 */}
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div className="order-2 lg:order-1 bg-gradient-to-br from-alabaster to-pearl-white rounded-2xl p-8 lg:p-12">
                <div className="space-y-4">
                  {[
                    { title: "Demand Forecasting", description: "AI-powered predictions for optimal stock levels" },
                    { title: "Automated Replenishment", description: "Smart reordering based on real-time analytics" },
                    { title: "Multi-Channel Sync", description: "Unified inventory across all sales channels" },
                    { title: "Warehouse Optimization", description: "Intelligent allocation and fulfillment routing" }
                  ].map((item, index) => (
                    <div key={index} className="bg-white p-4 rounded-xl shadow-sm">
                      <h4 className="font-semibold text-charcoal mb-1">{item.title}</h4>
                      <p className="text-sm text-titanium">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-block px-3 py-1 bg-primary-red/10 rounded-full mb-4">
                  <span className="text-primary-red font-semibold text-sm">Smart Inventory Management</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4 text-charcoal">
                  Optimize Stock & Fulfillment
                </h3>
                <p className="text-titanium mb-6">
                  Intelligent inventory management that reduces costs, prevents stockouts, and accelerates order fulfillment through predictive analytics and automation.
                </p>
                <ul className="space-y-3">
                  {[
                    "ML-based demand forecasting and trend analysis",
                    "Automated reorder points and safety stock calculations",
                    "Real-time inventory tracking across all locations",
                    "Smart allocation for faster fulfillment",
                    "Returns management and reverse logistics"
                  ].map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-graphite">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Solution 3 */}
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-block px-3 py-1 bg-primary-red/10 rounded-full mb-4">
                  <span className="text-primary-red font-semibold text-sm">Omnichannel Integration</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4 text-charcoal">
                  Seamless Cross-Channel Experience
                </h3>
                <p className="text-titanium mb-6">
                  Unify online, mobile, and in-store experiences with integrated systems that provide consistent, personalized customer journeys across all touchpoints.
                </p>
                <ul className="space-y-3">
                  {[
                    "Unified customer profiles across all channels",
                    "Buy online, pick up in store (BOPIS) capabilities",
                    "Mobile POS and clienteling for store associates",
                    "Consistent pricing and promotions everywhere",
                    "Centralized order management and fulfillment"
                  ].map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-graphite">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-alabaster to-pearl-white rounded-2xl p-8 lg:p-12">
                <div className="text-center mb-6">
                  <h4 className="text-lg font-semibold text-charcoal mb-2">Channel Integration</h4>
                  <p className="text-sm text-titanium">Unified customer experience across all touchpoints</p>
                </div>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {["Web", "Mobile", "Store", "Social", "Marketplace", "Voice"].map((channel, index) => (
                    <div key={index} className="bg-white p-3 rounded-lg shadow-sm text-center">
                      <p className="text-xs font-medium text-charcoal">{channel}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-primary-red/5 p-4 rounded-xl border-2 border-primary-red/20">
                  <div className="text-center">
                    <p className="text-sm font-semibold text-primary-red">Unified Platform</p>
                    <p className="text-xs text-titanium mt-1">Single source of truth for all operations</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advanced Features */}
      <section className="py-5 md:py-5 bg-gradient-to-br from-alabaster to-pearl-white">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Advanced E-commerce Capabilities
            </h2>
            <p className="text-lg text-titanium">
              Cutting-edge features that drive conversion and customer loyalty
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Dynamic Pricing Engine",
                description: "AI-powered pricing optimization based on demand, competition, and inventory levels",
                icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              },
              {
                title: "Customer Analytics",
                description: "Deep insights into shopping behavior, preferences, and lifetime value predictions",
                icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              },
              {
                title: "Fraud Prevention",
                description: "ML-based fraud detection and prevention systems to protect transactions",
                icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              },
              {
                title: "Payment Integration",
                description: "Seamless integration with multiple payment gateways and digital wallets",
                icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              },
              {
                title: "Loyalty Programs",
                description: "Automated reward systems and personalized loyalty incentives",
                icon: "M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
              },
              {
                title: "Marketing Automation",
                description: "Triggered campaigns, abandoned cart recovery, and personalized email marketing",
                icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              }
            ].map((feature, index) => (
              <div key={index} className="card bg-white group hover:shadow-xl transition-all duration-300">
                <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary-red/10 transition-colors">
                  <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={feature.icon} />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-charcoal">{feature.title}</h3>
                <p className="text-sm text-titanium">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Success Stories
            </h2>
            <p className="text-lg text-titanium">
              Real results from retail and e-commerce transformations
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {[
              {
                industry: "Fashion Retail",
                title: "50% Increase in Online Conversion",
                description: "Global fashion retailer implemented AI-powered personalization engine, resulting in dramatic improvements in customer engagement and sales.",
                metrics: [
                  { label: "Conversion Rate", value: "+50%" },
                  { label: "AOV", value: "+32%" },
                  { label: "Customer Retention", value: "+45%" }
                ]
              },
              {
                industry: "Grocery E-commerce",
                title: "40% Reduction in Stockouts",
                description: "Regional grocery chain deployed predictive inventory management, optimizing stock levels and reducing waste significantly.",
                metrics: [
                  { label: "Stockouts", value: "-40%" },
                  { label: "Inventory Costs", value: "-25%" },
                  { label: "Order Accuracy", value: "99.2%" }
                ]
              },
              {
                industry: "Consumer Electronics",
                title: "Seamless Omnichannel Experience",
                description: "Electronics retailer unified 200+ stores with online channels, creating consistent customer journeys across all touchpoints.",
                metrics: [
                  { label: "BOPIS Adoption", value: "+180%" },
                  { label: "Store Traffic", value: "+35%" },
                  { label: "NPS Score", value: "+28" }
                ]
              }
            ].map((useCase, index) => (
              <div key={index} className="card bg-gradient-to-br from-white to-alabaster hover:shadow-xl transition-all duration-300">
                <div className="inline-block px-3 py-1 bg-primary-red/10 rounded-full mb-4">
                  <span className="text-primary-red font-semibold text-xs">{useCase.industry}</span>
                </div>
                <h3 className="text-xl font-bold mb-3 text-charcoal">{useCase.title}</h3>
                <p className="text-sm text-titanium mb-6">{useCase.description}</p>
                <div className="grid grid-cols-3 gap-2 pt-6 border-t border-platinum">
                  {useCase.metrics.map((metric, idx) => (
                    <div key={idx} className="text-center">
                      <div className="text-lg font-bold text-primary-red">{metric.value}</div>
                      <div className="text-xs text-titanium">{metric.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Technology Excellence
            </h2>
            <p className="text-lg text-titanium">
              Built on industry-leading platforms and frameworks
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { category: "E-commerce Platforms", items: ["Shopify Plus", "Magento Commerce", "SAP Commerce Cloud", "Salesforce Commerce"] },
              { category: "AI & Analytics", items: ["TensorFlow", "PyTorch", "Google Analytics 4", "Segment CDP"] },
              { category: "Payment Systems", items: ["Stripe", "PayPal", "Adyen", "Square"] },
              { category: "Marketing Tools", items: ["Klaviyo", "Braze", "Optimizely", "Dynamic Yield"] }
            ].map((stack, index) => (
              <div key={index} className="card bg-white">
                <h3 className="font-semibold text-charcoal mb-4 pb-3 border-b border-platinum">{stack.category}</h3>
                <ul className="space-y-2">
                  {stack.items.map((item, idx) => (
                    <li key={idx} className="flex items-center text-sm text-titanium">
                      <svg className="w-4 h-4 text-primary-red mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Implementation Roadmap
            </h2>
            <p className="text-lg text-titanium">
              Structured approach to retail transformation
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {[
                {
                  phase: "Phase 1",
                  title: "Discovery & Strategy",
                  duration: "2-4 weeks",
                  activities: ["Business objectives assessment", "Current systems audit", "Customer journey mapping", "Solution architecture design"]
                },
                {
                  phase: "Phase 2",
                  title: "Platform Development",
                  duration: "8-12 weeks",
                  activities: ["E-commerce platform setup", "Custom feature development", "Third-party integrations", "Mobile app development"]
                },
                {
                  phase: "Phase 3",
                  title: "Data & AI Integration",
                  duration: "4-6 weeks",
                  activities: ["Data migration and cleansing", "AI model training", "Personalization engine setup", "Analytics implementation"]
                },
                {
                  phase: "Phase 4",
                  title: "Testing & Launch",
                  duration: "3-4 weeks",
                  activities: ["UAT and quality assurance", "Performance optimization", "Staff training", "Phased rollout and monitoring"]
                }
              ].map((step, index) => (
                <div key={index} className="flex gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-primary-red text-white rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                  </div>
                  <div className="flex-1 card">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3">
                      <div>
                        <span className="text-sm text-primary-red font-semibold">{step.phase}</span>
                        <h3 className="text-xl font-bold text-charcoal">{step.title}</h3>
                      </div>
                      <span className="text-sm text-titanium mt-2 md:mt-0">{step.duration}</span>
                    </div>
                    <ul className="grid md:grid-cols-2 gap-2">
                      {step.activities.map((activity, idx) => (
                        <li key={idx} className="flex items-start text-sm text-titanium">
                          <svg className="w-4 h-4 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          {activity}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 md:py-5 bg-gradient-to-br from-primary-red to-deep-garnet text-white">
        <div className="page-container text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Ready to Transform Your Retail Business?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Join leading retailers who have elevated their digital commerce with our AI-powered solutions. Let's discuss your transformation journey.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg">
                Schedule Consultation
              </Link>
              <Link to="/solutions" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
                Explore Solutions
              </Link>
            </div>
            <p className="mt-8 text-sm opacity-75">
              Free consultation • No commitment required • Expert guidance
            </p>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-5 bg-white">
        <div className="page-container">
          <h2 className="text-2xl font-bold mb-8 text-charcoal text-center">Related Services</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/services/ai-machine-learning" className="card hover:shadow-xl transition-all duration-300">
              <h3 className="font-semibold text-charcoal mb-2">AI & Machine Learning</h3>
              <p className="text-sm text-titanium mb-3">Intelligent systems for predictive analytics and automation</p>
              <span className="text-primary-red text-sm font-semibold">Learn More →</span>
            </Link>
            <Link to="/services/data-analytics" className="card hover:shadow-xl transition-all duration-300">
              <h3 className="font-semibold text-charcoal mb-2">Data Analytics</h3>
              <p className="text-sm text-titanium mb-3">Transform data into actionable business insights</p>
              <span className="text-primary-red text-sm font-semibold">Learn More →</span>
            </Link>
            <Link to="/services/custom-software" className="card hover:shadow-xl transition-all duration-300">
              <h3 className="font-semibold text-charcoal mb-2">Custom Software</h3>
              <p className="text-sm text-titanium mb-3">Tailored e-commerce and retail applications</p>
              <span className="text-primary-red text-sm font-semibold">Learn More →</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default RetailEcommerce;